
// File: src/pages/Login.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
// Using a custom button below for custom hover effects
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, ShieldCheck, CloudCog, LineChart, AlertCircle } from 'lucide-react';
import { APP_NAME, pageTitle } from '@/lib/appConfig';
import logo from '/vixalor-logo.webp';
import heroLogo from '/vixalor-login-logo.webp';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const { login } = useAuth();

  // Set page title
  useEffect(() => {
    document.title = pageTitle('Login');
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setIsLoading(true);
    try {
      await login(email, password);
    } catch (err: any) {
      const message = err.message || 'Login failed. Please check your credentials.';
      setFormError(message);
      toast.error(message);
      console.error('Login.tsx error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-10 overflow-hidden bg-[#eef2f9]">
      {/* Ambient background texture */}
      <div
        className="absolute inset-0 opacity-[0.5]"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(15,61,138,0.12) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />
      <div className="absolute -top-40 -left-40 h-96 w-96 rounded-full bg-sky-300/25 blur-[120px]" />
      <div className="absolute -bottom-40 -right-32 h-96 w-96 rounded-full bg-blue-400/20 blur-[120px]" />

      <div className="relative w-full max-w-6xl overflow-hidden rounded-3xl shadow-2xl border border-white/60 bg-white animate-login-in">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Left hero — hidden on small screens so the form is front and center */}
          <div
            className="hidden lg:flex relative text-white px-10 py-12 flex-col gap-8 overflow-hidden"
            style={{
              background:
                'radial-gradient(circle at 30% 25%, rgba(255,255,255,0.12), transparent 38%), radial-gradient(circle at 78% 8%, rgba(255,255,255,0.08), transparent 32%), linear-gradient(135deg, #0b2f73 0%, #0a2f63 45%, #0a3c8f 100%)',
            }}
          >
            {/* Subtle dot-grid texture for depth */}
            <div
              className="absolute inset-0 opacity-[0.15] pointer-events-none"
              style={{
                backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.6) 1px, transparent 1px)',
                backgroundSize: '24px 24px',
              }}
            />
            {/* Glow behind logo */}
            <div className="absolute left-8 top-10 h-80 w-80 rounded-full bg-cyan-200/30 blur-[130px]" />
            <div className="absolute -40 -30 h-96 w-96 rounded-full bg-sky-500/25 blur-[140px]" />

            <div className="relative flex items-center gap-4">
              <img src={heroLogo} alt={APP_NAME} className="h-24 w-auto drop-shadow-[0_16px_45px_rgba(0,0,0,0.45)]" />
            </div>

            <div className="relative space-y-4">
              <p className="text-4xl font-semibold leading-tight tracking-tight">
                Powering Smarter Retail Operations
              </p>
              <ul className="space-y-3 text-lg text-white/90">
                <li className="flex items-center gap-3">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/10 border border-white/20 backdrop-blur-sm text-white shadow-lg">
                    <ShieldCheck className="h-4 w-4" />
                  </span>
                  Secure
                </li>
                <li className="flex items-center gap-3">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/10 border border-white/20 backdrop-blur-sm text-white shadow-lg">
                    <CloudCog className="h-4 w-4" />
                  </span>
                  Cloud Based
                </li>
                <li className="flex items-center gap-3">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/10 border border-white/20 backdrop-blur-sm text-white shadow-lg">
                    <LineChart className="h-4 w-4" />
                  </span>
                  Real-time Analytics
                </li>
              </ul>
            </div>
          </div>

          {/* Right form */}
          <div className="p-8 sm:p-10 lg:p-12 bg-white flex flex-col justify-center">
            {/* Mobile-only brand mark, since the hero panel is hidden below lg */}
            <div className="flex lg:hidden items-center gap-3 mb-8">
              <img src={logo} alt={APP_NAME} className="h-10 w-auto" />
            </div>

            <Card className="border-none shadow-none">
              <CardHeader className="px-0">
                <p className="text-sm font-semibold text-[#0f3d8a] tracking-wide mb-1">Welcome back</p>
                <CardTitle className="text-3xl font-semibold text-slate-900">Sign In</CardTitle>
                <CardDescription className="text-base text-slate-600">
                  Enter your credentials to access your dashboard.
                </CardDescription>
              </CardHeader>

              <form onSubmit={handleSubmit} noValidate>
                <CardContent className="space-y-6 px-0">
                  {formError && (
                    <div className="flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 animate-shake">
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span>{formError}</span>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Email</Label>
                    <div className="relative group">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 transition-colors group-focus-within:text-[#0f3d8a]" />
                      <Input
                        id="email"
                        type="email"
                        autoComplete="username"
                        placeholder="you@example.com"
                        value={email}
                        onChange={e => { setEmail(e.target.value); if (formError) setFormError(null); }}
                        disabled={isLoading}
                        required
                        className={[
                          'pl-12 h-12 rounded-xl bg-slate-50 text-slate-900 placeholder:text-slate-400',
                          'border-slate-200 transition-all duration-150',
                          'focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#0f3d8a] focus-visible:border-transparent',
                          formError ? 'border-red-300' : '',
                        ].join(' ')}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password" className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Password</Label>
                    </div>
                    <div className="relative group">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 transition-colors group-focus-within:text-[#0f3d8a]" />
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        value={password}
                        onChange={e => { setPassword(e.target.value); if (formError) setFormError(null); }}
                        disabled={isLoading}
                        required
                        className={[
                          'pl-12 pr-14 h-12 rounded-xl bg-slate-50 text-slate-900 placeholder:text-slate-400',
                          'border-slate-200 transition-all duration-150',
                          'focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-[#0f3d8a] focus-visible:border-transparent',
                          formError ? 'border-red-300' : '',
                        ].join(' ')}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 focus:outline-none"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    <p className="text-xs text-slate-400 pt-1">Forgot your password? Contact your administrator.</p>
                  </div>
                </CardContent>

                <CardFooter className="px-0 pt-2 flex-col gap-4">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={[
                      'w-full h-12 rounded-xl font-semibold text-white tracking-wide',
                      'bg-gradient-to-r from-[#0a2a5f] via-[#0f3d8a] to-[#0a2a5f]',
                      'shadow-[0_10px_30px_rgba(15,61,138,0.35)] relative overflow-hidden',
                      'transition-all duration-200 ease-out',
                      'hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(15,61,138,0.45)]',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0f3d8a] focus-visible:ring-offset-white',
                      isLoading ? 'opacity-90 cursor-progress' : 'cursor-pointer'
                    ].join(' ')}
                  >
                    <span className="absolute inset-0 pointer-events-none ripple" />
                    {isLoading ? (
                      <div className="flex items-center justify-center gap-3">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Signing In...
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <span>Sign In</span>
                        <span className="text-lg leading-none">→</span>
                      </div>
                    )}
                  </button>

                  <p className="text-xs text-slate-400 text-center w-full">
                    © {new Date().getFullYear()} {APP_NAME}. All rights reserved.
                  </p>
                </CardFooter>
              </form>
            </Card>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes loginIn {
          0% { opacity: 0; transform: translateY(14px) scale(0.99); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-login-in {
          animation: loginIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-4px); }
          40% { transform: translateX(4px); }
          60% { transform: translateX(-3px); }
          80% { transform: translateX(3px); }
        }
        .animate-shake {
          animation: shake 0.4s ease-in-out;
        }
        .ripple::before, .ripple::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 9999px;
          background: radial-gradient(circle, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 60%);
          transform: scale(0);
          opacity: 0;
          animation: rippleWave 2s infinite;
        }
        .ripple::after {
          animation-delay: 1s;
        }
        @keyframes rippleWave {
          0% { transform: scale(0); opacity: 0.35; }
          60% { transform: scale(1.6); opacity: 0; }
          100% { transform: scale(1.8); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
